import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControlLabel,
  Switch,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  ColorLens as ColorIcon,
} from '@mui/icons-material';
import { vehicleService } from '../services/api';
import { Vehicle } from '../types';
import { divertilandiaColors } from '../theme/divertilandiaColors';

const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    year: '',
    plate_number: '',
    fuel_type: 'regular' as 'regular' | 'premium' | 'diesel',
    vehicle_type: 'regular' as 'regular' | 'hybrid',
    fuel_efficiency: 0,
    battery_capacity_kwh: 0,
    electric_range_km: 0,
    battery_min_percentage: 25,
    hybrid_efficiency_km_per_liter: 0,
    color: '#2196F3',
    is_active: true,
  });

  const fuelTypes = [
    { value: 'regular', label: 'Regular' },
    { value: 'premium', label: 'Premium' },
    { value: 'diesel', label: 'Diésel' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const vehiclesData = await vehicleService.getAll();
      setVehicles(vehiclesData);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle?: Vehicle) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        name: vehicle.name,
        model: vehicle.model || '',
        year: vehicle.year || '',
        plate_number: vehicle.plate_number || '',
        fuel_type: vehicle.fuel_type,
        vehicle_type: vehicle.vehicle_type || 'regular',
        fuel_efficiency: vehicle.fuel_efficiency,
        battery_capacity_kwh: vehicle.battery_capacity_kwh || 0,
        electric_range_km: vehicle.electric_range_km || 0,
        battery_min_percentage: vehicle.battery_min_percentage || 25,
        hybrid_efficiency_km_per_liter: vehicle.hybrid_efficiency_km_per_liter || 0,
        color: vehicle.color,
        is_active: vehicle.is_active,
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        name: '',
        model: '',
        year: '',
        plate_number: '',
        fuel_type: 'regular',
        vehicle_type: 'regular',
        fuel_efficiency: 0,
        battery_capacity_kwh: 0,
        electric_range_km: 0,
        battery_min_percentage: 25,
        hybrid_efficiency_km_per_liter: 0,
        color: '#2196F3',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVehicle(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (editingVehicle) {
        await vehicleService.update(editingVehicle.id, formData);
      } else {
        await vehicleService.create(formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (err) {
      setError('Error al guardar el vehículo');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
      try {
        await vehicleService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el vehículo');
        console.error(err);
      }
    }
  };

  const getFuelTypeLabel = (type: string) => {
    const fuelType = fuelTypes.find(ft => ft.value === type);
    return fuelType ? fuelType.label : type;
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ color: divertilandiaColors.standard.primary }}>
          Gestión de Vehículos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ backgroundColor: divertilandiaColors.rainbow.violet }}
        >
          Agregar Vehículo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {vehicles.map((vehicle) => (
          <Grid item xs={12} md={6} lg={4} key={vehicle.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: vehicle.color,
                        borderRadius: '50%',
                        mr: 1,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {vehicle.name}
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                    <Chip
                      label={vehicle.is_active ? 'Activo' : 'Inactivo'}
                      sx={{
                        backgroundColor: vehicle.is_active ? divertilandiaColors.rainbow.green : divertilandiaColors.standard.neutral,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    {vehicle.vehicle_type === 'hybrid' && (
                      <Chip
                        label="Híbrido"
                        size="small"
                        sx={{
                          backgroundColor: divertilandiaColors.rainbow.blue,
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {vehicle.model && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                  </Typography>
                )}

                {vehicle.plate_number && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Placa: {vehicle.plate_number}
                  </Typography>
                )}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <FuelIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.orange, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getFuelTypeLabel(vehicle.fuel_type)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <SpeedIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.blue, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {vehicle.fuel_efficiency} km/L
                      </Typography>
                    </Box>
                  </Grid>
                  {vehicle.vehicle_type === 'hybrid' && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Batería:</strong> {vehicle.battery_capacity_kwh} kWh
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Rango Eléctrico:</strong> {vehicle.electric_range_km} km
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Híbrido:</strong> {vehicle.hybrid_efficiency_km_per_liter} km/L
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <MoneyIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.green, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Precio actual en sistema
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Costo calculado automáticamente
                    </Typography>
                  </Grid>
                </Grid>

                <Box display="flex" gap={1} mt={2}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(vehicle)}
                    sx={{ flex: 1 }}
                  >
                    Editar
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(vehicle.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog para agregar/editar vehículo */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVehicle ? 'Editar Vehículo' : 'Agregar Nuevo Vehículo'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Vehículo"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Modelo"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Año"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Placa"
                value={formData.plate_number}
                onChange={(e) => handleInputChange('plate_number', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Vehículo</InputLabel>
                <Select
                  value={formData.vehicle_type}
                  onChange={(e) => handleInputChange('vehicle_type', e.target.value)}
                  label="Tipo de Vehículo"
                >
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="hybrid">Híbrido Enchufable</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Combustible</InputLabel>
                <Select
                  value={formData.fuel_type}
                  onChange={(e) => handleInputChange('fuel_type', e.target.value)}
                  label="Tipo de Combustible"
                >
                  {fuelTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rendimiento (km/litro)"
                type="number"
                value={formData.fuel_efficiency}
                onChange={(e) => handleInputChange('fuel_efficiency', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.1 }}
                required
                helperText={formData.vehicle_type === 'hybrid' ? 'Rendimiento en modo híbrido (PHEV)' : 'Rendimiento en km/litro'}
              />
            </Grid>

            {/* Campos para vehículos híbridos */}
            {formData.vehicle_type === 'hybrid' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Capacidad de Batería (kWh)"
                    type="number"
                    value={formData.battery_capacity_kwh}
                    onChange={(e) => handleInputChange('battery_capacity_kwh', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.1 }}
                    required
                    helperText="Ej: 8.3 kWh"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rango Eléctrico (km)"
                    type="number"
                    value={formData.electric_range_km}
                    onChange={(e) => handleInputChange('electric_range_km', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.1 }}
                    required
                    helperText="Kilómetros en modo 100% eléctrico (Ej: 50 km)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Porcentaje Mínimo de Batería (%)"
                    type="number"
                    value={formData.battery_min_percentage}
                    onChange={(e) => handleInputChange('battery_min_percentage', parseFloat(e.target.value) || 25)}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    helperText="Porcentaje al que cambia a modo híbrido (default: 25%)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rendimiento Híbrido (km/litro)"
                    type="number"
                    value={formData.hybrid_efficiency_km_per_liter}
                    onChange={(e) => handleInputChange('hybrid_efficiency_km_per_liter', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.1 }}
                    required
                    helperText="Rendimiento combinado PHEV (Ej: 25 km/L)"
                  />
                </Grid>
              </>
            )}


            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Vehículo Activo"
              />
            </Grid>

            {formData.fuel_efficiency > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    Información del Vehículo
                  </Typography>
                  {formData.vehicle_type === 'hybrid' ? (
                    <>
                      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                        El costo se calculará automáticamente considerando:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        • Rango eléctrico efectivo: {formData.electric_range_km ? (formData.electric_range_km * (1 - (formData.battery_min_percentage || 25) / 100)).toFixed(1) : '0'} km
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        • Capacidad de batería: {formData.battery_capacity_kwh} kWh
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        • Rendimiento híbrido: {formData.hybrid_efficiency_km_per_liter} km/L
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Para distancias ≤ {formData.electric_range_km ? (formData.electric_range_km * (1 - (formData.battery_min_percentage || 25) / 100)).toFixed(1) : '0'} km se usará solo electricidad. 
                        Para distancias mayores, se combinará electricidad y gasolina.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        El costo por kilómetro se calculará automáticamente usando los precios actuales de combustible del sistema.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        Rendimiento: {formData.fuel_efficiency} km/L
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingVehicle ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vehicles;
